import { useCallback, useMemo, useRef } from "react"

import { DEFAULT_PAGE_SIZE } from "@/constants/pagination"

import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"
import { normalizeConversationTransportRow } from "@/utils/conversationsTransportRows"

import { useConversationsFilter } from "../contexts/ConversationsFilterContext"
import {
  useConversationsKeysetInfiniteTransport,
  type ConversationKeysetRequest,
} from "../routes/useConversationsKeysetTransport"
import type { ConversationSearchType as ConversationsTransportFiltersSearchType } from "../types"
import { buildConversationsTransportFilters } from "../utils/conversationsTransportFilters"
import { useConversationsSimulationOutputRestriction } from "./useConversationsSimulationOutputRestriction"

type ConversationKeysetRequestWithTagFilters = Omit<
  ConversationKeysetRequest,
  "filters"
> & {
  filters?: NonNullable<ConversationKeysetRequest["filters"]> & {
    tag_filters?: string[]
    search_query?: string
  }
}

const QUERY_KEY_BASE = "uploaded-conversations-table-keyset"

/**
 * Hook for infinite-scroll uploaded conversation data using keyset pagination.
 * Returns data, pagination controls, and loading states.
 */
export function useConversationsInfiniteQuery({
  enabled = true,
  searchQuery = "",
  searchType,
  simulationOutputIds,
}: {
  enabled?: boolean
  searchQuery?: string
  searchType?: ConversationsTransportFiltersSearchType
  /**
   * Restricts results to exactly these conversations (e.g. one visualizer cluster's
   * members), combined with the other filters via AND on the backend. Undefined/empty
   * applies no restriction. An active human review project filter narrows it further.
   */
  simulationOutputIds?: string[]
} = {}) {
  const {
    state: {
      dateRange,
      metadataFilters,
      metricIds,
      agentIds,
      tagFilters,
      metricValueFilters,
    },
  } = useConversationsFilter()
  const restriction = useConversationsSimulationOutputRestriction({
    clusterIds: simulationOutputIds,
    enabled,
  })
  // Until the project filter resolves to ids, the request carries no
  // restriction, so cached rows for it would be unfiltered and are hidden.
  const isRestrictionUnresolved =
    restriction.isPending || restriction.isError || restriction.matchesNothing

  const filters = useMemo(
    () =>
      buildConversationsTransportFilters({
        searchQuery,
        searchType,
        agentIds,
        dateRange,
        metadataFilters,
        metricIds,
        metricValueFilters,
        tagFilters,
      }),
    [
      searchQuery,
      searchType,
      agentIds,
      dateRange,
      metadataFilters,
      metricIds,
      metricValueFilters,
      tagFilters,
    ],
  )

  const request = useMemo(
    (): ConversationKeysetRequestWithTagFilters => ({
      limit: DEFAULT_PAGE_SIZE,
      filters,
      simulation_output_ids: restriction.simulationOutputIds,
    }),
    [filters, restriction.simulationOutputIds],
  )

  // Query key for cache management
  const queryKey = useMemo(
    () =>
      [
        QUERY_KEY_BASE,
        filters,
        restriction.simulationOutputIds,
        restriction.matchesNothing,
      ] as const,
    [filters, restriction.simulationOutputIds, restriction.matchesNothing],
  )

  // Use transport infinite query
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching: isKeysetFetching,
    isFetchingNextPage,
    isLoading: isKeysetLoading,
    isPlaceholderData,
    isError: isKeysetError,
    error: keysetError,
    refetch: refetchKeyset,
    isFetched,
  } = useConversationsKeysetInfiniteTransport({
    enabled: enabled && !isRestrictionUnresolved,
    request,
  })

  // Once the tier-1 table query settles, keep the flag true for this mount so
  // deferred filter lookups do not re-enter loading state on filter changes. A
  // failed project lookup settles it too: the keyset request stays disabled
  // behind it, and the filter menu must load to show its own error and retry.
  const hasSettledRef = useRef(false)
  if (
    isFetched ||
    (enabled && (restriction.matchesNothing || restriction.isError))
  ) {
    hasSettledRef.current = true
  }

  // Whether the rows last shown came from a project-restricted request.
  const shownRowsHadProjectFilterRef = useRef(false)
  if (data && !isPlaceholderData && !isRestrictionUnresolved) {
    shownRowsHadProjectFilterRef.current = restriction.hasProjectFilter
  }

  // Placeholder rows come from the previous request. When either that request
  // or this one carries a project filter, they may not match the current
  // filter (a project subset shown as unfiltered, or vice versa), so they are
  // hidden too.
  const isHidingRows =
    isRestrictionUnresolved ||
    (isPlaceholderData &&
      (restriction.hasProjectFilter || shownRowsHadProjectFilterRef.current))

  const isError = restriction.isError || isKeysetError
  const error = restriction.isError ? restriction.error : keysetError
  const refetchRestriction = restriction.refetch
  const refetch = useCallback(
    () => (restriction.isError ? refetchRestriction() : refetchKeyset()),
    [restriction.isError, refetchRestriction, refetchKeyset],
  )

  // Flatten all pages into a single array, deduplicating by simulationOutputId
  const allRows = useMemo((): ConversationsTableResult[] => {
    if (!data?.pages || isHidingRows) {
      return []
    }
    const seen = new Set<string>()
    return data.pages
      .flatMap((page) =>
        page.items.map((row) => normalizeConversationTransportRow(row)),
      )
      .filter((row) => {
        if (!row.simulation_output_id || seen.has(row.simulation_output_id)) {
          return false
        }
        seen.add(row.simulation_output_id)
        return true
      })
  }, [data?.pages, isHidingRows])

  // Get all loaded cursors (page boundaries) for session storage
  const loadedCursors = useMemo((): (string | undefined)[] => {
    if (!data?.pageParams || isHidingRows) {
      return []
    }
    return data.pageParams.map((pageParam) => pageParam?.cursor ?? undefined)
  }, [data?.pageParams, isHidingRows])

  // Find row index by simulation_output_id
  const findRowIndex = useCallback(
    (simulationOutputId: string): number => {
      return allRows.findIndex(
        (row) => row.simulation_output_id === simulationOutputId,
      )
    },
    [allRows],
  )

  // Get row by simulation_output_id
  const getRow = useCallback(
    (simulationOutputId: string): ConversationsTableResult | undefined => {
      return allRows.find(
        (row) => row.simulation_output_id === simulationOutputId,
      )
    },
    [allRows],
  )

  // Get adjacent rows for j/k navigation
  const getAdjacentRows = useCallback(
    (
      currentId: string,
    ): {
      prev: ConversationsTableResult | undefined
      next: ConversationsTableResult | undefined
    } => {
      const currentIndex = findRowIndex(currentId)
      if (currentIndex === -1) {
        return { prev: undefined, next: undefined }
      }
      return {
        prev: currentIndex > 0 ? allRows[currentIndex - 1] : undefined,
        next:
          currentIndex < allRows.length - 1
            ? allRows[currentIndex + 1]
            : undefined,
      }
    },
    [allRows, findRowIndex],
  )

  // Suppress pagination while showing placeholder data from a previous query
  const gatedHasNextPage =
    isPlaceholderData || isRestrictionUnresolved
      ? false
      : (hasNextPage ?? false)

  // Prefetch next page when near the end
  const prefetchNextPage = useCallback(() => {
    if (gatedHasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [gatedHasNextPage, isFetchingNextPage, fetchNextPage])

  return {
    // Data
    data: allRows,
    totalLoaded: allRows.length,
    loadedCursors,

    projectFilterTruncatedFromCount: restriction.truncatedFromCount,

    // Pagination
    fetchNextPage,
    hasNextPage: gatedHasNextPage,
    prefetchNextPage,

    // Loading states
    hasSettledInitialFetch: hasSettledRef.current,
    isLoading:
      isKeysetLoading ||
      restriction.isPending ||
      (isHidingRows && !restriction.isError && !restriction.matchesNothing),
    isFetching: isKeysetFetching || restriction.isFetching,
    isFetchingNextPage,
    isPlaceholderData,
    isError,
    error,

    // Utilities
    refetch,
    findRowIndex,
    getRow,
    getAdjacentRows,
    queryKey,
  }
}
